import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { 
  getAllCardTemplates, 
  createCardTemplate, 
  updateCardTemplate,
  deleteCardTemplate,
  initializeCardTemplatesFromJson,
  getCardTemplatesCount
} from '../../../services/cardTemplatesService';

export const useCardTemplatesLogic = () => {
  const [cardTemplates, setCardTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedSubstep, setSelectedSubstep] = useState(null);
  const [optionInput, setOptionInput] = useState('');

  // Template form data
  const [templateFormData, setTemplateFormData] = useState({
    id: '',
    type: 'stage',
    title: '',
    description: '',
    additionalInfo: '',
    order: 0,
    substeps: [],
    icon: {
      type: 'document',
      path: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
    }
  });

  // Substep form data
  const [substepFormData, setSubstepFormData] = useState({
    id: '',
    title: '',
    description: '',
    question: '',
    type: 'text',
    placeholder: '',
    helpText: '',
    options: [],
    defaultValue: '',
    followUp: null
  });

  // Load templates on component mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        setError('');
        
        const templates = await getAllCardTemplates();
        setCardTemplates(templates);
      } catch (error) {
        console.error('Error loading card templates:', error);
        setError('Failed to load card templates');
      } finally {
        setLoading(false);
      }
    };
    
    loadTemplates();
  }, []);

  // Function to select a template
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setEditMode(false);
    setSelectedSubstep(null);
    setSubstepFormData({
      id: '',
      title: '',
      description: '',
      question: '',
      type: 'text',
      placeholder: '',
      helpText: '',
      options: [],
      defaultValue: '',
      followUp: null
    });
  };

  // Function to create a new template
  const handleCreateNewTemplate = async () => {
    try {
      const count = await getCardTemplatesCount();
      
      setTemplateFormData({
        id: `template_${uuidv4().substring(0, 8)}`,
        type: 'stage',
        title: '',
        description: '',
        additionalInfo: '',
        order: count,
        substeps: [],
        icon: {
          type: 'document',
          path: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
        }
      });
      
      setSelectedTemplate(null);
      setEditMode(true);
      setSelectedSubstep(null);
    } catch (error) {
      console.error('Error creating new template:', error);
      setError('Failed to create new template');
    }
  };

  // Function to edit a template
  const handleEditTemplate = () => {
    if (!selectedTemplate) return;
    
    setTemplateFormData({
      ...selectedTemplate
    });
    
    setEditMode(true);
  };

  // Function to cancel editing a template
  const handleCancelEditTemplate = () => {
    setEditMode(false);
    
    if (selectedTemplate) {
      // If we were editing an existing template, reset form data and keep the template selected
      setTemplateFormData({
        ...selectedTemplate
      });
    } else {
      // If we were creating a new template, clear form data
      setTemplateFormData({
        id: '',
        type: 'stage',
        title: '',
        description: '',
        additionalInfo: '',
        order: 0,
        substeps: [],
        icon: {
          type: 'document',
          path: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
        }
      });
    }
  };

  // Function to save a template
  const handleSaveTemplate = async () => {
    try {
      // Validate required fields
      if (!templateFormData.id || !templateFormData.title) {
        setError('Template ID and title are required');
        return;
      }
      
      // Check if we're creating or updating
      if (selectedTemplate) {
        // Update existing template
        await updateCardTemplate(selectedTemplate.id, templateFormData);
      } else {
        // Create new template
        await createCardTemplate(templateFormData);
      }
      
      // Refresh templates list
      const templates = await getAllCardTemplates();
      setCardTemplates(templates);
      
      // Update selected template if we were editing
      if (selectedTemplate) {
        const updatedTemplate = templates.find(t => t.id === selectedTemplate.id);
        setSelectedTemplate(updatedTemplate);
      }
      
      // Exit edit mode
      setEditMode(false);
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
      setError('');
    } catch (error) {
      console.error('Error saving template:', error);
      setError('Failed to save template');
    }
  };

  // Function to delete a template
  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;
    
    if (!window.confirm(`Are you sure you want to delete template "${selectedTemplate.title}"?`)) {
      return;
    }
    
    try {
      await deleteCardTemplate(selectedTemplate.id);
      
      // Refresh templates list
      const templates = await getAllCardTemplates();
      setCardTemplates(templates);
      
      // Clear selected template
      setSelectedTemplate(null);
      setTemplateFormData({
        id: '',
        type: 'stage',
        title: '',
        description: '',
        additionalInfo: '',
        order: 0,
        substeps: [],
        icon: {
          type: 'document',
          path: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
        }
      });
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
      setError('');
    } catch (error) {
      console.error('Error deleting template:', error);
      setError('Failed to delete template');
    }
  };

  // Function to handle form changes for template
  const handleTemplateFormChange = (e) => {
    const { name, value } = e.target;
    setTemplateFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Functions for substeps

  // Create a new substep
  const handleCreateNewSubstep = () => {
    setSelectedSubstep(null);
    setSubstepFormData({
      id: `substep_${uuidv4().substring(0, 8)}`,
      title: '',
      description: '',
      question: '',
      type: 'text',
      placeholder: '',
      helpText: '',
      options: [],
      defaultValue: '',
      followUp: null
    });
  };

  // Select a substep for editing
  const handleSelectSubstep = (substep) => {
    setSelectedSubstep(substep);
    setSubstepFormData({
      ...substep
    });
  };

  // Handle form changes for substep
  const handleSubstepFormChange = (e) => {
    const { name, value } = e.target;
    setSubstepFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Add option to substep options
  const handleAddOption = () => {
    if (!optionInput.trim()) return;
    
    setSubstepFormData(prev => ({
      ...prev,
      options: [...(prev.options || []), optionInput.trim()]
    }));
    
    setOptionInput('');
  };

  // Remove option from substep options
  const handleRemoveOption = (optionToRemove) => {
    setSubstepFormData(prev => ({
      ...prev,
      options: (prev.options || []).filter(option => option !== optionToRemove)
    }));
  };

  // Save substep
  const handleSaveSubstep = () => {
    // Validate required fields
    if (!substepFormData.id || !substepFormData.title || !substepFormData.question) {
      setError('Substep ID, title and question are required');
      return;
    }
    
    // Update templateFormData with the new or updated substep
    setTemplateFormData(prev => {
      const updatedSubsteps = [...prev.substeps];
      
      if (selectedSubstep) {
        // Update existing substep
        const index = updatedSubsteps.findIndex(s => s.id === selectedSubstep.id);
        if (index !== -1) {
          updatedSubsteps[index] = substepFormData;
        }
      } else {
        // Add new substep
        updatedSubsteps.push(substepFormData);
      }
      
      return {
        ...prev,
        substeps: updatedSubsteps
      };
    });
    
    // Clear form and selection
    setSelectedSubstep(null);
    setSubstepFormData({
      id: '',
      title: '',
      description: '',
      question: '',
      type: 'text',
      placeholder: '',
      helpText: '',
      options: [],
      defaultValue: '',
      followUp: null
    });
    
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  // Delete substep
  const handleDeleteSubstep = () => {
    if (!selectedSubstep) return;
    
    if (!window.confirm(`Are you sure you want to delete substep "${selectedSubstep.title}"?`)) {
      return;
    }
    
    setTemplateFormData(prev => ({
      ...prev,
      substeps: prev.substeps.filter(s => s.id !== selectedSubstep.id)
    }));
    
    setSelectedSubstep(null);
    setSubstepFormData({
      id: '',
      title: '',
      description: '',
      question: '',
      type: 'text',
      placeholder: '',
      helpText: '',
      options: [],
      defaultValue: '',
      followUp: null
    });
    
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  // Initialize from JSON
  const handleInitializeFromJson = async () => {
    if (!window.confirm('Warning: This will replace all existing templates. Are you sure you want to continue?')) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Import JSON
      const jsonData = await import('../../../mocks/deploymentCards.json');
      
      // Initialize templates
      await initializeCardTemplatesFromJson(jsonData.default);
      
      // Refresh templates list
      const templates = await getAllCardTemplates();
      setCardTemplates(templates);
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
      setError('');
    } catch (error) {
      console.error('Error initializing templates from JSON:', error);
      setError('Failed to initialize templates from JSON');
    } finally {
      setLoading(false);
    }
  };

  return {
    cardTemplates,
    loading,
    error,
    saveSuccess,
    selectedTemplate,
    editMode,
    templateFormData,
    selectedSubstep,
    substepFormData,
    optionInput,
    handleSelectTemplate,
    handleCreateNewTemplate,
    handleEditTemplate,
    handleCancelEditTemplate,
    handleSaveTemplate,
    handleDeleteTemplate,
    handleTemplateFormChange,
    handleCreateNewSubstep,
    handleSelectSubstep,
    handleSubstepFormChange,
    handleAddOption,
    handleRemoveOption,
    handleSaveSubstep,
    handleDeleteSubstep,
    handleInitializeFromJson,
    setOptionInput
  };
}; 